/**
 * Form fields used across the dashboard.
 *
 * HeroUI renders fields with the `primary` variant by default, which has the same tone as cards
 * and dialogs in dark mode. Every field here sits inside a card or a dialog, so it uses the
 * `secondary` variant to stay visible (HeroUI guidance for fields on a surface).
 */
import {
  Checkbox,
  Description,
  FieldError,
  Input,
  Label,
  ListBox,
  Select,
  Switch,
  TextArea,
  TextField,
} from "@heroui/react";
import type { ReactNode } from "react";

interface FieldBase {
  label: ReactNode;
  description?: ReactNode;
  error?: string | null;
  isRequired?: boolean;
  isDisabled?: boolean;
}

export function TextInput({
  label,
  description,
  error,
  isRequired,
  isDisabled,
  value,
  onChange,
  name,
  type = "text",
  placeholder,
  autoFocus,
  autoComplete,
  mono,
  min,
  max,
}: FieldBase & {
  value: string;
  onChange: (value: string) => void;
  name: string;
  type?: "text" | "email" | "password" | "number" | "url";
  placeholder?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  mono?: boolean;
  min?: number;
  max?: number;
}) {
  return (
    <TextField
      fullWidth
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      isRequired={isRequired}
      isDisabled={isDisabled}
      isInvalid={Boolean(error)}
      autoFocus={autoFocus}
    >
      <Label>{label}</Label>
      <Input
        variant="secondary"
        placeholder={placeholder}
        autoComplete={autoComplete}
        min={min}
        max={max}
        className={mono ? "font-mono text-[13px]" : undefined}
      />
      {description && !error ? <Description>{description}</Description> : null}
      {error ? <FieldError>{error}</FieldError> : null}
    </TextField>
  );
}

export function TextAreaInput({
  label,
  description,
  error,
  isRequired,
  isDisabled,
  value,
  onChange,
  name,
  rows = 6,
  placeholder,
  mono,
}: FieldBase & {
  value: string;
  onChange: (value: string) => void;
  name: string;
  rows?: number;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <TextField
      fullWidth
      name={name}
      value={value}
      onChange={onChange}
      isRequired={isRequired}
      isDisabled={isDisabled}
      isInvalid={Boolean(error)}
    >
      <Label>{label}</Label>
      <TextArea
        variant="secondary"
        rows={rows}
        placeholder={placeholder}
        spellCheck={mono ? false : undefined}
        className={mono ? "font-mono text-[13px] leading-relaxed" : undefined}
      />
      {description && !error ? <Description>{description}</Description> : null}
      {error ? <FieldError>{error}</FieldError> : null}
    </TextField>
  );
}

export interface SelectOption {
  id: string;
  label: string;
  hint?: string;
}

export function SelectInput({
  label,
  description,
  error,
  isRequired,
  isDisabled,
  value,
  onChange,
  options,
  placeholder,
}: FieldBase & {
  value: string | null;
  onChange: (value: string | null) => void;
  options: SelectOption[];
  placeholder?: string;
}) {
  return (
    <Select
      fullWidth
      variant="secondary"
      placeholder={placeholder}
      value={value}
      onChange={(v) => onChange(v === null ? null : String(v))}
      isRequired={isRequired}
      isDisabled={isDisabled}
      isInvalid={Boolean(error)}
    >
      <Label>{label}</Label>
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      {description && !error ? <Description>{description}</Description> : null}
      {error ? <FieldError>{error}</FieldError> : null}
      <Select.Popover>
        <ListBox>
          {options.map((o) => (
            <ListBox.Item key={o.id} id={o.id} textValue={o.label}>
              <div className="flex flex-col py-0.5">
                <span>{o.label}</span>
                {o.hint ? <span className="text-xs text-muted">{o.hint}</span> : null}
              </div>
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  );
}

export function SwitchInput({
  label,
  description,
  isSelected,
  onChange,
  isDisabled,
}: {
  label: ReactNode;
  description?: ReactNode;
  isSelected: boolean;
  onChange: (value: boolean) => void;
  isDisabled?: boolean;
}) {
  return (
    <Switch isSelected={isSelected} onChange={onChange} isDisabled={isDisabled}>
      <Switch.Content>
        <Switch.Control>
          <Switch.Thumb />
        </Switch.Control>
        <span className="text-sm font-medium">{label}</span>
      </Switch.Content>
      {description ? <Description>{description}</Description> : null}
    </Switch>
  );
}

export function CheckboxInput({
  label,
  isSelected,
  onChange,
  isDisabled,
  variant = "secondary",
}: {
  label: ReactNode;
  isSelected: boolean;
  onChange: (value: boolean) => void;
  isDisabled?: boolean;
  /** `primary` when the checkbox sits on the page background instead of a card. */
  variant?: "primary" | "secondary";
}) {
  return (
    <Checkbox variant={variant} isSelected={isSelected} onChange={onChange} isDisabled={isDisabled}>
      <Checkbox.Content>
        <Checkbox.Control>
          <Checkbox.Indicator />
        </Checkbox.Control>
        <span className="text-sm">{label}</span>
      </Checkbox.Content>
    </Checkbox>
  );
}

/** Vertical stack with the standard gap between fields. */
export function FieldStack({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-5">{children}</div>;
}
